import type { Table } from "@tanstack/react-table";
import type { CustomerRow } from "../../types/crm";
import { PRODUCT_CAPSULE_LABELS } from "../../types/crm";
import { PMS_LABEL, PRODUCT_LABEL } from "./columns";

const escapeCsvField = (value: string): string => {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

// ISO 8601 — either full datetime ("2026-05-08T19:35:06.000Z") or
// date-only ("2026-05-08"). Strict enough that hotel names / IDs don't
// false-match.
const ISO_DATE_RE =
  /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

// Mirrors `formatDate` in columns.tsx so CSV reads the same way as the UI.
const formatDateForCsv = (value: Date): string =>
  value.toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const formatCellForCsv = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") {
    return Number.isFinite(value) ? value.toLocaleString("en-US") : "";
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value instanceof Date) return formatDateForCsv(value);
  if (typeof value === "string" && ISO_DATE_RE.test(value)) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return formatDateForCsv(d);
  }
  return String(value);
};

const headerLabel = (header: unknown, fallback: string): string => {
  if (typeof header === "string") return header;
  // `headerTip` attaches `.label` to its render function for this purpose.
  if (typeof header === "function") {
    const maybeLabel = (header as { label?: unknown }).label;
    if (typeof maybeLabel === "string") return maybeLabel;
  }
  return fallback;
};

// Columns that store a fraction in the data model (0.3) but render as a
// whole-number percent on screen ("30%"). Keep the CSV in lock-step with
// the dashboard so a reader doesn't get a different number than they see.
const PERCENT_COLUMN_IDS = new Set<string>([
  "commissionRate",
  "autumnShareOfTraffic",
  "autumnShareOfBookings",
  "brandProtectionPct",
  "occupancyLift30d",
  "adrChange30d",
  "revParImprovement30d",
  "ctr",
  "revParYoY",
  "adrYoY",
  "occupancyYoY",
  "revParSinceGoLive",
]);

const formatPercentForCsv = (value: unknown): string => {
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  return `${Math.round(value * 100)}%`;
};

// Columns rendered as USD on screen via `formatCurrency` with
// `maximumFractionDigits: 0` — the dashboard always displays whole
// dollars (e.g. "$1,234"). Round to integer in CSV so a founder's
// spreadsheet totals exactly match what's on the page.
const CURRENCY_COLUMN_IDS = new Set<string>([
  "carr",
  "totalCommissionYtd",
  "lastCommissionBill",
  "lastCharge",
  "fixedFeeMonthly",
  "monthlyRevenue",
  "bookingsValueUsd30d",
  "revenue30d",
  "spend30d",
  "cac",
  "revenueLiftSinceGoLiveUsd",
]);

const formatCurrencyForCsv = (value: unknown): string => {
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  return Math.round(value).toLocaleString("en-US");
};

const todayMdy = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${m}_${d}_${y}`;
};

/**
 * Export the visible rows and visible columns of the CRM customer table to
 * a CSV file. Respects current filters, sorting, column order, and column
 * visibility — i.e. whatever the user sees on screen.
 */
export const exportCustomersToCsv = (table: Table<CustomerRow>): void => {
  const columns = table.getVisibleLeafColumns();
  const rows = table.getRowModel().rows;

  const headers = columns.flatMap((col) => {
    // The `acvArr` column toggles between two precomputed projections on
    // screen; in the CSV we emit both so a reader doesn't have to flip a
    // toggle that doesn't exist in a spreadsheet.
    if (col.id === "acvArr") {
      return [
        escapeCsvField("ARR (Latest Month)"),
        escapeCsvField("ARR (Trailing 3-mo Avg)"),
      ];
    }
    return [escapeCsvField(headerLabel(col.columnDef.header, col.id))];
  });

  const body = rows.map((row, idx) =>
    columns
      .flatMap((col) => {
        // `rowNumber` is a display-only column (no accessor), so
        // `row.getValue` returns undefined. Match the on-screen #
        // cell, which renders the row's 1-based position in the
        // sorted+filtered view we're already iterating.
        if (col.id === "rowNumber") return [String(idx + 1)];
        // `products` is also display-only — the real data lives at
        // `row.original.productCapsules`. Mirror the UI's chip list
        // by mapping codes through the canonical label map.
        if (col.id === "products") {
          const capsules = row.original.productCapsules ?? [];
          return [capsules.map((c) => PRODUCT_CAPSULE_LABELS[c]).join(", ")];
        }
        // `daysToRenewal` is null when the customer is on a
        // month-to-month contract; the UI shows "M2M" italic in that
        // case, so we mirror it here instead of leaving the cell
        // ambiguous-blank.
        if (col.id === "daysToRenewal") {
          const v = row.getValue(col.id);
          return [v == null ? "M2M" : formatCellForCsv(v)];
        }
        // `acvArr` expands into two cells (Latest, 3-mo Avg) — read both
        // precomputed projections straight off the row so the values
        // match the dashboard toggle exactly (no client-side recompute).
        if (col.id === "acvArr") {
          return [
            formatCurrencyForCsv(row.original.billing.acvArrDefault),
            formatCurrencyForCsv(row.original.billing.acvArrAvg3),
          ];
        }
        // Enum-shaped accessor columns whose UI renders a friendly label.
        // CSV emits the same label so a finance reader doesn't see raw
        // codes like "all_in_one" / "charge_failed" / "mews".
        if (col.id === "productType") {
          const v = row.getValue(col.id);
          return [typeof v === "string" ? PRODUCT_LABEL[v] ?? v : ""];
        }
        if (col.id === "pms") {
          const v = row.getValue(col.id);
          return [
            typeof v === "string" ? PMS_LABEL[v.toLowerCase()] ?? v : "",
          ];
        }
        if (col.id === "churnRisk") {
          // Accessor returns the severity rank (0/1/2) for sorting; the
          // human-readable level lives on `row.original.churnRisk.level`.
          const lvl = row.original.churnRisk?.level;
          return [lvl ? lvl.charAt(0).toUpperCase() + lvl.slice(1) : ""];
        }
        // `owners` is display-only — surface the assigned CSM emails so
        // ownership shows up in the export instead of a blank cell.
        if (col.id === "owners") {
          const owners = row.original.owners ?? [];
          return [owners.map((o) => o.adminEmail).join(", ")];
        }
        if (CURRENCY_COLUMN_IDS.has(col.id)) {
          return [formatCurrencyForCsv(row.getValue(col.id))];
        }
        if (PERCENT_COLUMN_IDS.has(col.id)) {
          return [formatPercentForCsv(row.getValue(col.id))];
        }
        return [formatCellForCsv(row.getValue(col.id))];
      })
      .map(escapeCsvField)
      .join(","),
  );

  const csv = [headers.join(","), ...body].join("\n");
  // Prepend a UTF-8 BOM so Excel for Windows opens the file with the
  // correct encoding (without it, accented hotel names like "Métropole"
  // come through mojibake'd).
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `crm_${todayMdy()}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};
